import { has, omit } from 'ramda'

import { DEFAULT_SENDER_ID_VALUE } from '@/app/definitions'
import { cannot } from '@/app/helpers'
import { LanguageType } from '@/app/module/campaigns/helpers'
import { changeActionsLang, processText } from '@/app/module/campaigns/language-helpers'
import {
  BackendCampaignTemplate,
  CampaignTemplateType,
  Launch,
  PartFamily,
  PartTypes,
  ReminderCampaignItem,
  ReminderDefaultArgs,
  ReminderMessage,
  ReminderPart,
} from '@/app/module/campaigns/types'
import { ReminderCampaignDTO } from '@/app/module/campaigns/types/dtos'
import type { CampaignSaveData } from '@/app/module/campaigns/types/save-data'
import { ZReminderCampaignResponse } from '@/app/module/campaigns/types/schemas/campaigns/reminder'
import {
  processActionForSaving,
  processActionsInLists,
  processActionsInObjects,
  processOpenEndedActions,
} from '@/app/module/campaigns/utils/process-actions'
import { generateCampaignName } from '@/app/module/campaigns/utils/transform'
import { formatDateTime } from '@/app/service/util'
import { captureError } from '@/app/service/util/error'

export type { ReminderCampaignItem } from '@/app/module/campaigns/types'

export const isReminderCampaign = (obj: unknown): obj is ReminderCampaignItem => {
  if (!obj || typeof obj !== 'object') {
    return false
  }

  // UI model shape
  if ('type' in obj && (obj.type === CampaignTemplateType.Reminder || obj.type === 'reminder')) {
    return true
  }

  // Backend DTO shape
  if ('template' in obj && (obj.template === BackendCampaignTemplate.Reminder || obj.template === 'reminder')) {
    return true
  }

  return false
}

export const createEmptyReminderCampaignItem = (): Partial<ReminderCampaignItem> => ({
  type: CampaignTemplateType.Reminder,
  variables: {
    defaultArgs: {
      t0: '',
      t0timezone: '',
    },
    parts: [],
    senderId: '',
    senderIdQuestions: '',
    senderIdReplies: '',
  },
})

export const reminderGetPart = (part: ReminderPart): ReminderPart => {
  const { id, label, offset, when } = part

  if (has(PartTypes.ActionsOnly, part)) {
    return {
      id,
      label,
      offset,
      when,
      [PartTypes.ActionsOnly]: part[PartTypes.ActionsOnly],
    }
  }

  if (has(PartTypes.QuotaActions, part)) {
    return {
      id,
      label,
      offset,
      when,
      [PartTypes.QuotaActions]: part[PartTypes.QuotaActions],
    }
  }

  if (has(PartTypes.SMSMessage, part)) {
    return {
      id,
      label,
      offset,
      when,
      [PartTypes.SMSMessage]: part[PartTypes.SMSMessage],
    }
  }

  if (has(PartTypes.SMSQuestion, part)) {
    return {
      id,
      label,
      offset,
      when,
      [PartTypes.SMSQuestion]: part[PartTypes.SMSQuestion],
    }
  }

  return cannot(part)
}

export const reminderPartToMessage = (part: ReminderPart): ReminderMessage => {
  const baseMessage = {
    id: part.id,
    changed: false,
    label: part.label,
    offset: part.offset,
    when: part.when,
  }

  if (has(PartTypes.ActionsOnly, part)) {
    return {
      ...baseMessage,
      hasQuestion: true,
      type: PartFamily.Action,
      [PartTypes.ActionsOnly]: part[PartTypes.ActionsOnly],
    }
  }

  if (has(PartTypes.QuotaActions, part)) {
    return {
      ...baseMessage,
      hasQuestion: true,
      type: PartFamily.QuotaActions,
      [PartTypes.QuotaActions]: part[PartTypes.QuotaActions],
    }
  }

  if (has(PartTypes.SMSMessage, part)) {
    return {
      ...baseMessage,
      ...part[PartTypes.SMSMessage],
      hasQuestion: false,
      type: PartFamily.SMS,
    }
  }

  if (has(PartTypes.SMSQuestion, part)) {
    return {
      ...baseMessage,
      ...part[PartTypes.SMSQuestion],
      hasQuestion: true,
      type: PartFamily.SMS,
      onAnswer: part[PartTypes.SMSQuestion].onAnswer || [],
      onInvalidReply: {
        actions: part[PartTypes.SMSQuestion].onInvalidReply?.actions || [],
      },
      onRetriesExhausted: {
        actions: part[PartTypes.SMSQuestion].onRetriesExhausted?.actions || [],
      },
      onTimeout: {
        actions: part[PartTypes.SMSQuestion].onTimeout?.actions || [],
        timeout: part[PartTypes.SMSQuestion].onTimeout?.timeout,
        timeoutSeconds: part[PartTypes.SMSQuestion].onTimeout?.timeoutSeconds,
      },
      openEnded: part[PartTypes.SMSQuestion].openEnded || undefined,
    }
  }

  return cannot(part)
}

/**
 * Returns a list of messages indexed by the partId for multimessage component
 */
export const reminderPartsToMessages = (parts: ReminderPart[]) =>
  parts.reduce<Record<string, ReminderMessage>>((acc, part) => {
    const message = reminderPartToMessage(reminderGetPart(part))

    return {
      ...acc,
      [message.id]: message,
    }
  }, {})

/**
 * Returns an array of parts based on the messages provided
 */
export const reminderMessagesToParts = (messages: Record<string, ReminderMessage>, orderedIds: string[]) =>
  orderedIds.map((id) => {
    const message = messages[id]
    const { label, offset } = message

    switch (true) {
      case message.type === PartFamily.Action:
        return {
          id,
          label,
          offset,
          when: message.when,
          [PartTypes.ActionsOnly]: message.actionsOnly,
        }
      case message.type === PartFamily.QuotaActions:
        return {
          id,
          label,
          offset,
          when: message.when,
          [PartTypes.QuotaActions]: message.quotaActions,
        }
      case message.type === PartFamily.SMS && !message.hasQuestion:
        return {
          id,
          label,
          offset,
          when: message.when,
          [PartTypes.SMSMessage]: {
            message: message.message,
          },
        }
      case message.type === PartFamily.SMS && message.hasQuestion:
        return {
          id,
          label,
          offset,
          when: message.when,
          [PartTypes.SMSQuestion]: {
            message: message.message,
            onAnswer: message.onAnswer || [],
            onInvalidReply: {
              actions: message.onInvalidReply?.actions || [],
            },
            onRetriesExhausted: {
              actions: message.onRetriesExhausted?.actions || [],
            },
            onTimeout: {
              actions: message.onTimeout?.actions || [],
              timeout: message.onTimeout?.timeout,
              timeoutSeconds: message.onTimeout?.timeoutSeconds,
            },
            openEnded: message.openEnded || undefined,
            retries: message.retries,
            useAi: message.useAi,
          },
        }

      default:
        return cannot(message)
    }
  })

export const updateReminderDefaultArgs = (item: ReminderCampaignItem, data: ReminderDefaultArgs) => {
  return {
    ...item,
    variables: {
      ...item.variables,
      defaultArgs: {
        ...(item.variables?.defaultArgs || {}),
        ...data,
      },
    },
  }
}

export const changeReminderLanguage = (
  item: ReminderCampaignItem,
  newLanguages: LanguageType[],
  newDefaultLanguage: string,
): ReminderCampaignItem => {
  const { languages, defaultLanguage } = item.uiStore || {}
  const languageConfig = { languages, newLanguages, defaultLanguage, newDefaultLanguage }

  const parts = (item.variables?.parts || []).map((part) => {
    if (has(PartTypes.ActionsOnly, part)) {
      const typedPart = part
      return {
        ...part,
        [PartTypes.ActionsOnly]: processActionsInObjects(
          typedPart[PartTypes.ActionsOnly],
          languageConfig,
          changeActionsLang,
        ),
      }
    }
    if (has(PartTypes.QuotaActions, part)) {
      return {
        ...part,
        [PartTypes.QuotaActions]: {
          onExhausted:
            processActionsInObjects(
              { actions: part[PartTypes.QuotaActions].onExhausted },
              languageConfig,
              changeActionsLang,
            )?.actions || [],
          onOpen:
            processActionsInObjects({ actions: part[PartTypes.QuotaActions].onOpen }, languageConfig, changeActionsLang)
              ?.actions || [],
        },
      }
    }
    if (has(PartTypes.SMSMessage, part)) {
      const typedPart = part
      return {
        ...part,
        [PartTypes.SMSMessage]: {
          message: processText(typedPart[PartTypes.SMSMessage].message, languageConfig),
        },
      }
    }
    if (has(PartTypes.SMSQuestion, part)) {
      const typedPart = part
      return {
        ...part,
        [PartTypes.SMSQuestion]: {
          ...typedPart[PartTypes.SMSQuestion],
          message: processText(typedPart[PartTypes.SMSQuestion].message, languageConfig),
          onAnswer: processActionsInLists(typedPart[PartTypes.SMSQuestion].onAnswer, languageConfig, changeActionsLang),
          onInvalidReply: processActionsInObjects(
            typedPart[PartTypes.SMSQuestion].onInvalidReply,
            languageConfig,
            changeActionsLang,
          ),
          onRetriesExhausted: processActionsInObjects(
            typedPart[PartTypes.SMSQuestion].onRetriesExhausted,
            languageConfig,
            changeActionsLang,
          ),
          onTimeout: processActionsInObjects(
            typedPart[PartTypes.SMSQuestion].onTimeout,
            languageConfig,
            changeActionsLang,
          ),
          openEnded: processOpenEndedActions(
            typedPart[PartTypes.SMSQuestion].openEnded,
            languageConfig,
            changeActionsLang,
          ),
        },
      }
    }

    return part
  }) as ReminderPart[]

  return {
    ...item,
    uiStore: {
      ...(item.uiStore || {}),
      languages: newLanguages,
      defaultLanguage: newDefaultLanguage,
    },
    variables: {
      ...item.variables,
      parts,
    },
  }
}

export const cleanupReminderActionsInVariablesForSaving = (item: ReminderCampaignItem): ReminderPart[] => {
  const { senderId, senderIdQuestions } = item.variables || {}

  return (item.variables?.parts || []).map((vPart) => {
    const part = reminderGetPart(vPart)

    if (has(PartTypes.ActionsOnly, part)) {
      return {
        ...part,
        [PartTypes.ActionsOnly]: processActionsInObjects(
          part[PartTypes.ActionsOnly],
          { senderId, senderIdQuestions },
          processActionForSaving,
        ),
      } as ReminderPart
    }

    if (has(PartTypes.QuotaActions, part)) {
      return {
        ...part,
        [PartTypes.QuotaActions]: {
          onExhausted:
            processActionsInObjects(
              { actions: part[PartTypes.QuotaActions].onExhausted },
              { senderId, senderIdQuestions },
              processActionForSaving,
            )?.actions || [],
          onOpen:
            processActionsInObjects(
              { actions: part[PartTypes.QuotaActions].onOpen },
              { senderId, senderIdQuestions },
              processActionForSaving,
            )?.actions || [],
        },
      } as ReminderPart
    }

    if (has(PartTypes.SMSQuestion, part)) {
      return {
        ...part,
        [PartTypes.SMSQuestion]: {
          ...part[PartTypes.SMSQuestion],
          onAnswer: processActionsInLists(
            part[PartTypes.SMSQuestion].onAnswer,
            { senderId, senderIdQuestions },
            processActionForSaving,
          ),
          onInvalidReply: processActionsInObjects(
            part[PartTypes.SMSQuestion].onInvalidReply,
            { senderId, senderIdQuestions },
            processActionForSaving,
          ),
          onRetriesExhausted: processActionsInObjects(
            part[PartTypes.SMSQuestion].onRetriesExhausted,
            { senderId, senderIdQuestions },
            processActionForSaving,
          ),
          onTimeout: processActionsInObjects(
            part[PartTypes.SMSQuestion].onTimeout,
            { senderId, senderIdQuestions },
            processActionForSaving,
          ),
          openEnded: processOpenEndedActions(
            part[PartTypes.SMSQuestion].openEnded,
            { senderId, senderIdQuestions },
            processActionForSaving,
          ),
        },
      } as ReminderPart
    }

    return part
  })
}

const getLaunch = (launch?: Launch): Launch => {
  if (!launch || launch.immediate) {
    return { immediate: true }
  }

  return {
    ...launch,
    at: launch.at ? formatDateTime(launch.at, launch.timezone || '') : undefined,
  }
}

export const reminderItemToDTO = (item: ReminderCampaignItem): CampaignSaveData<ReminderCampaignDTO> => {
  const { variables } = item
  const { defaultSenderId, senderId, senderIdQuestions, senderIdReplies } = variables || {}
  const name = generateCampaignName({
    name: item.name,
    type: CampaignTemplateType.Reminder,
  })

  const launch: Launch = getLaunch(item.launch)

  return {
    ...omit(['type'], item),
    template: BackendCampaignTemplate.Reminder,
    name,
    published: item.published || false,
    launchOpen: item.launchOpen === undefined ? true : item.launchOpen,
    launch,
    variables: {
      ...variables,
      defaultSenderId,
      senderId: senderId === DEFAULT_SENDER_ID_VALUE ? '' : senderId,
      senderIdQuestions,
      senderIdReplies,
      parts: cleanupReminderActionsInVariablesForSaving(item),
    },
  }
}

export const transformReminderDTOToItem = (dto: unknown) => {
  const parsed = ZReminderCampaignResponse.safeParse(dto)
  if (!parsed.success) {
    captureError(parsed.error)
    return undefined
  }
  const validated = parsed.data

  return {
    ...omit(['template'], validated),
    type: CampaignTemplateType.Reminder,
  } satisfies ReminderCampaignItem
}
